import { useCallback, useEffect, useState } from "react";
import type {
  Invitation,
  Page,
  PublicUser,
  WorkspaceMember,
  WorkspaceSummary,
} from "@sol-office/contracts";
import { api, patch, post, errorMessage } from "../api";
import { ErrorNotice, Spinner, Modal, Field, formatDate } from "../ui";

export function JoinInvitation({
  token,
  user,
  onJoined,
  onCancel,
}: {
  token: string;
  user: PublicUser;
  onJoined: (workspace: WorkspaceSummary) => Promise<void>;
  onCancel: () => void;
}) {
  const [pending, setPending] = useState(false),
    [error, setError] = useState("");
  const accept = async () => {
    setPending(true);
    setError("");
    try {
      const result = await post<{ workspace: WorkspaceSummary }>(
        "/invitations/accept",
        { token },
      );
      await onJoined(result.workspace);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="boot-screen">
      <section className="panel join-invitation">
        <h1>Join your team.</h1>
        <p>
          Accept this invitation as <strong>{user.email}</strong>.
        </p>
        <p>
          The invitation must be addressed to this account and can be used once.
        </p>
        {error && <ErrorNotice message={error} />}
        <div className="modal-actions">
          <button
            className="button secondary"
            disabled={pending}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="button primary"
            disabled={pending}
            onClick={() => void accept()}
          >
            {pending ? "Joining…" : "Accept invitation"}
          </button>
        </div>
      </section>
    </div>
  );
}

export function MembershipPage({
  workspace,
  user,
  revision,
  onInvite,
  onChanged,
}: {
  workspace: WorkspaceSummary;
  user: PublicUser;
  revision: number;
  onInvite?: () => void;
  onChanged: () => Promise<void>;
}) {
  const [members, setMembers] = useState<WorkspaceMember[]>([]),
    [cursor, setCursor] = useState<string | null>(null);
  const [invites, setInvites] = useState<Invitation[]>([]),
    [inviteCursor, setInviteCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true),
    [pending, setPending] = useState(false),
    [error, setError] = useState("");
  const [selected, setSelected] = useState<WorkspaceMember | null>(null),
    [transfer, setTransfer] = useState<WorkspaceMember | null>(null),
    [share, setShare] = useState<{ url: string; email: string } | null>(null),
    [copied, setCopied] = useState(false);
  const manager = ["owner", "admin"].includes(workspace.role);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const m = await api<Page<WorkspaceMember>>(
        `/workspaces/${workspace.id}/members?limit=50`,
      );
      setMembers(m.items);
      setCursor(m.nextCursor);
      if (manager) {
        const i = await api<Page<Invitation>>(
          `/workspaces/${workspace.id}/invites?limit=50`,
        );
        setInvites(i.items);
        setInviteCursor(i.nextCursor);
      } else {
        setInvites([]);
        setInviteCursor(null);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [workspace.id, manager]);
  useEffect(() => {
    void load();
  }, [load, revision]);
  const act = async (action: () => Promise<unknown>) => {
    setPending(true);
    setError("");
    try {
      await action();
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPending(false);
    }
  };
  const manage = (m: WorkspaceMember) =>
    workspace.role === "owner" ||
    (workspace.role === "admin" && m.role === "member");
  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">THE PEOPLE BEHIND THE WORK</span>
          <h1>Better, together.</h1>
          <p>Members and invitations for {workspace.name}.</p>
        </div>
        {onInvite && (
          <button className="button primary" onClick={onInvite}>
            Invite people
          </button>
        )}
      </div>
      {error && <ErrorNotice message={error} retry={() => void load()} />}
      <section className="panel">
        <div className="panel-heading">
          <h2>Workspace members</h2>
          <span>{workspace.name}</span>
        </div>
        {loading && !members.length ? (
          <Spinner label="Loading members…" />
        ) : (
          <div className="member-list">
            {members.map((m) => (
              <div className="member-row" key={m.id}>
                <span className="avatar member-avatar">
                  {m.user.name.slice(0, 2)}
                </span>
                <div>
                  <strong>{m.user.name}</strong>
                  <span>{m.user.email}</span>
                </div>
                <span className={`role-badge ${m.role}`}>{m.role}</span>
                <small>
                  {m.status === "suspended"
                    ? "Suspended"
                    : `Joined ${formatDate(m.joinedAt)}`}
                </small>
                {manager && manage(m) && (
                  <button
                    className="button secondary member-action"
                    disabled={pending}
                    onClick={() => setSelected(m)}
                    aria-label={`Manage ${m.user.email}`}
                  >
                    Manage
                  </button>
                )}
                {workspace.role === "owner" &&
                  m.user.id !== user.id &&
                  m.status === "active" &&
                  m.role !== "guest" && (
                    <button
                      className="button secondary member-action"
                      disabled={pending}
                      onClick={() => setTransfer(m)}
                      aria-label={`Transfer ownership to ${m.user.email}`}
                    >
                      Transfer ownership
                    </button>
                  )}
              </div>
            ))}
          </div>
        )}
        {cursor && (
          <div className="load-more">
            <button
              className="button secondary"
              disabled={pending}
              onClick={() =>
                void (async () => {
                  setPending(true);
                  try {
                    const page = await api<Page<WorkspaceMember>>(
                      `/workspaces/${workspace.id}/members?limit=50&cursor=${encodeURIComponent(cursor)}`,
                    );
                    setMembers((v) => [...v, ...page.items]);
                    setCursor(page.nextCursor);
                  } catch (e) {
                    setError(errorMessage(e));
                  } finally {
                    setPending(false);
                  }
                })()
              }
            >
              Load more members
            </button>
          </div>
        )}
      </section>
      {manager && (
        <section className="panel invitations-panel">
          <div className="panel-heading">
            <h2>Invitations</h2>
            <span className="subtle-label">One-time links</span>
          </div>
          <p className="panel-description">
            Create a link and share it with the named person. Creating another
            link invalidates their earlier link. Email delivery is not
            connected.
          </p>
          {!invites.length && (
            <p className="panel-description">No invitations yet.</p>
          )}
          <div className="member-list">
            {invites.map((i) => (
              <div className="invitation-row" key={i.id}>
                <div>
                  <strong>{i.email}</strong>
                  <small>
                    {i.role} · {i.status} · Expires {formatDate(i.expiresAt)}
                  </small>
                </div>
                {i.status === "pending" && (
                  <div className="invitation-actions">
                    <button
                      className="button secondary"
                      disabled={pending}
                      onClick={() =>
                        void act(async () => {
                          const result = await post<{ url: string }>(
                            `/workspaces/${workspace.id}/invites/${i.id}/link`,
                            { expectedVersion: i.version },
                          );
                          setShare({ url: result.url, email: i.email });
                          setCopied(false);
                        })
                      }
                    >
                      Create join link
                    </button>
                    <button
                      className="button secondary"
                      disabled={pending}
                      onClick={() =>
                        void act(() =>
                          post(
                            `/workspaces/${workspace.id}/invites/${i.id}/revoke`,
                            { expectedVersion: i.version },
                          ),
                        )
                      }
                    >
                      Revoke
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {inviteCursor && (
            <button
              className="button secondary"
              disabled={pending}
              onClick={() =>
                void (async () => {
                  setPending(true);
                  try {
                    const page = await api<Page<Invitation>>(
                      `/workspaces/${workspace.id}/invites?limit=50&before=${inviteCursor}`,
                    );
                    setInvites((v) => [...v, ...page.items]);
                    setInviteCursor(page.nextCursor);
                  } catch (e) {
                    setError(errorMessage(e));
                  } finally {
                    setPending(false);
                  }
                })()
              }
            >
              Load more invitations
            </button>
          )}
        </section>
      )}
      {selected && (
        <Modal
          title={`Manage ${selected.user.name}`}
          subtitle={selected.user.email}
          onClose={() => !pending && setSelected(null)}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              void act(async () => {
                await patch(
                  `/workspaces/${workspace.id}/members/${selected.user.id}`,
                  {
                    expectedVersion: selected.version,
                    ...(selected.role === "owner"
                      ? {}
                      : { role: data.get("role") }),
                    status: data.get("status"),
                  },
                );
                setSelected(null);
                await onChanged();
              });
            }}
          >
            {selected.role !== "owner" && (
              <Field label="Role">
                <select name="role" defaultValue={selected.role}>
                  <option value="member">Member</option>
                  {workspace.role === "owner" && (
                    <option value="admin">Admin</option>
                  )}
                </select>
              </Field>
            )}
            <Field label="Access">
              <select name="status" defaultValue={selected.status}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </Field>
            <p>
              Suspension removes access to this workspace, including open
              documents. Work is preserved.
            </p>
            {error && <ErrorNotice message={error} />}
            <div className="modal-actions">
              <button
                type="button"
                className="button secondary"
                onClick={() => setSelected(null)}
                disabled={pending}
              >
                Cancel
              </button>
              <button className="button primary" disabled={pending}>
                Save membership
              </button>
            </div>
          </form>
        </Modal>
      )}
      {transfer && (
        <Modal
          title="Transfer workspace ownership"
          subtitle={workspace.name}
          onClose={() => !pending && setTransfer(null)}
        >
          <p>
            <strong>{transfer.user.name}</strong> will become an owner. Your
            role will change to admin.
          </p>
          {error && <ErrorNotice message={error} />}
          <div className="modal-actions">
            <button
              className="button secondary"
              disabled={pending}
              onClick={() => setTransfer(null)}
            >
              Cancel
            </button>
            <button
              className="button primary"
              disabled={pending}
              onClick={() =>
                void act(async () => {
                  const { membership: me } = await api<{
                    membership: { version: number };
                  }>(`/workspaces/${workspace.id}/members/me`);
                  await post(`/workspaces/${workspace.id}/ownership`, {
                    userId: transfer.user.id,
                    expectedVersion: me.version,
                  });
                  setTransfer(null);
                  await onChanged();
                })
              }
            >
              Confirm transfer
            </button>
          </div>
        </Modal>
      )}
      {share && (
        <Modal
          title="Share this invitation"
          subtitle={share.email}
          onClose={() => setShare(null)}
        >
          <p>
            Only this person’s signed-in account can accept. This link is shown
            here once; create a new link if needed.
          </p>
          <Field label="Join link">
            <input
              readOnly
              value={share.url}
              onFocus={(e) => e.currentTarget.select()}
            />
          </Field>
          <div className="modal-actions">
            <button className="button secondary" onClick={() => setShare(null)}>
              Done
            </button>
            <button
              className="button primary"
              onClick={() => {
                void navigator.clipboard
                  .writeText(share.url)
                  .then(() => setCopied(true))
                  .catch(() => setError("Select and copy the link manually."));
              }}
            >
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

type SessionItem = {
  id: string;
  current: boolean;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
};
export function SessionsPanel() {
  const [items, setItems] = useState<SessionItem[]>([]),
    [error, setError] = useState(""),
    [pending, setPending] = useState(false);
  const load = useCallback(async () => {
    try {
      setItems((await api<{ items: SessionItem[] }>("/auth/sessions")).items);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <section className="panel settings-panel">
      <div className="panel-heading">
        <h2>Active sessions</h2>
      </div>
      {error && <ErrorNotice message={error} retry={() => void load()} />}
      <div className="member-list">
        {items.map((s) => (
          <div className="invitation-row" key={s.id}>
            <div>
              <strong>
                {s.current ? "This session" : "Another signed-in session"}
              </strong>
              <small>
                Signed in {formatDate(s.createdAt)} · Last active{" "}
                {new Date(s.lastSeenAt).toLocaleString()}
              </small>
            </div>
            <button
              className="button secondary"
              disabled={pending}
              onClick={() =>
                void (async () => {
                  setPending(true);
                  setError("");
                  try {
                    await post(`/auth/sessions/${s.id}/revoke`, {});
                    if (s.current)
                      window.dispatchEvent(new Event("sol:session-expired"));
                    else await load();
                  } catch (e) {
                    setError(errorMessage(e));
                  } finally {
                    setPending(false);
                  }
                })()
              }
            >
              Sign out{s.current ? "" : " session"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
